import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { AlertCircle } from 'lucide-react';

interface BlockDayModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (days: number[], reason?: string) => Promise<boolean>;
  clientId: number;
  year: number;
  month: number;
  initialDays?: number[];
}

const BlockDayModal: React.FC<BlockDayModalProps> = ({
  open,
  onClose,
  onConfirm,
  clientId,
  year,
  month,
  initialDays = []
}) => {
  const [selectedDays, setSelectedDays] = useState<number[]>(initialDays);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  const monthName = monthNames[month - 1];
  const lastDayOfMonth = new Date(year, month, 0).getDate();
  const today = new Date();
  const currentDay = today.getDate();
  const isCurrentMonth = today.getFullYear() === year && today.getMonth() + 1 === month;

  const handleDayToggle = (day: number) => {
    if (selectedDays.includes(day)) {
      setSelectedDays(selectedDays.filter(d => d !== day));
    } else {
      // Validar que não está selecionando dia no passado
      if (isCurrentMonth && day < currentDay) {
        setError('Não é possível bloquear dias no passado');
        return;
      }
      setError(null);
      setSelectedDays([...selectedDays, day].sort((a, b) => a - b));
    }
  };

  const handleSubmit = async () => {
    if (selectedDays.length === 0) {
      setError('Selecione pelo menos um dia para bloquear');
      return;
    }

    // Validar que não está bloqueando dias no passado
    if (isCurrentMonth) {
      const pastDays = selectedDays.filter(day => day < currentDay);
      if (pastDays.length > 0) {
        setError(`Não é possível bloquear dias no passado: ${pastDays.join(', ')}`);
        return;
      }
    }

    setLoading(true);
    setError(null);

    try {
      const success = await onConfirm(selectedDays, reason || undefined);
      if (success) {
        setSelectedDays([]);
        setReason('');
        onClose();
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao bloquear dias');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setSelectedDays([]);
      setReason('');
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Bloquear Dias - {monthName} {year}</DialogTitle>
          <DialogDescription>
            Selecione os dias que deseja bloquear. Nenhum evento será executado nos dias bloqueados.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <div>
            <Label>Dias do mês</Label>
            <div className="grid grid-cols-7 gap-2 mt-2">
              {Array.from({ length: lastDayOfMonth }, (_, i) => i + 1).map((day) => {
                const isPast = isCurrentMonth && day < currentDay;
                const isSelected = selectedDays.includes(day);
                const isDisabled = isPast;

                return (
                  <button
                    key={day}
                    type="button"
                    onClick={() => !isDisabled && handleDayToggle(day)}
                    disabled={isDisabled}
                    className={`
                      p-2 border rounded-lg text-sm font-medium transition-all
                      ${isDisabled 
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                        : isSelected
                          ? 'bg-red-100 border-red-300 text-red-700'
                          : 'bg-white border-gray-300 hover:bg-gray-50'
                      }
                    `}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
            {selectedDays.length > 0 && (
              <p className="text-sm text-gray-600 mt-2">
                Dias selecionados: {selectedDays.join(', ')}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="reason">Motivo (opcional)</Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex: Feriado, manutenção, etc."
              className="mt-1"
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || selectedDays.length === 0}
            className="bg-red-600 hover:bg-red-700"
          >
            {loading ? 'Bloqueando...' : 'Bloquear Dias'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BlockDayModal;

